import { LucideIcon, Music, CalendarDays, TreePine, Mic } from 'lucide-react';

interface Holiday {
  id: string;
  title: string;
  imageSrc: string;
  altText: string;
  description: string;
  featured?: boolean;
}

interface SequenceType {
  id: string;
  title: string;
  icon: LucideIcon;
  altText: string;
  description: string;
}

interface Genre {
  id: string;
  title: string;
  icon: LucideIcon;
  altText: string;
  description: string;
}

interface Prop {
  id: string;
  title: string;
  icon: LucideIcon;
  altText: string;
  description: string;
}

interface Combination {
  title: string;
  href: string;
  description: string;
}

export const holidays: Holiday[] = [
  {
    id: 'christmas',
    title: 'Christmas',
    imageSrc: '/images/categories/christmas.jpg',
    altText: 'Christmas light sequences with decorated trees and houses',
    description: 'Festive light shows for the holiday season',
    featured: true
  },
  {
    id: 'halloween',
    title: 'Halloween',
    imageSrc: '/images/categories/halloween.jpg',
    altText: 'Spooky Halloween light sequences',
    description: 'Spooky and fun sequences for Halloween displays'
  },
  {
    id: '4th-of-july',
    title: '4th of July',
    imageSrc: '/images/categories/fourth-july.jpg',
    altText: 'Patriotic light sequences for Independence Day',
    description: 'Patriotic displays for Independence Day celebrations'
  }
];

export const sequenceTypes: SequenceType[] = [
  {
    id: 'lyrics',
    title: 'Lyrics',
    icon: Music,
    altText: 'Lyrics-based light sequences',
    description: 'Light shows synchronized to song lyrics'
  },
  {
    id: 'singing-faces',
    title: 'Singing Faces',
    icon: Mic,
    altText: 'Singing face light sequences',
    description: 'Animated faces that sing along with the music'
  },
  {
    id: 'props',
    title: 'Props',
    icon: TreePine,
    altText: 'Prop-based light sequences',
    description: 'Sequences designed for specific light display props'
  }
];

export const genres: Genre[] = [
  {
    id: 'rock',
    title: 'Rock',
    icon: Music,
    altText: 'Rock music light sequences',
    description: 'Light shows set to rock music classics'
  },
  {
    id: 'pop',
    title: 'Pop',
    icon: Music,
    altText: 'Pop music light sequences',
    description: 'Popular music light show sequences'
  },
  {
    id: 'jazz',
    title: 'Jazz',
    icon: Music,
    altText: 'Jazz music light sequences',
    description: 'Smooth jazz holiday light shows'
  },
  {
    id: 'hip-hop',
    title: 'Hip-Hop',
    icon: Music,
    altText: 'Hip-Hop music light sequences',
    description: 'Modern hip-hop light sequences'
  }
];

export const props: Prop[] = [
  {
    id: 'pixel-tree',
    title: 'Pixel Tree',
    icon: TreePine,
    altText: 'Pixel tree light sequences',
    description: 'Sequences designed for pixel Christmas trees'
  },
  {
    id: 'matrix-board',
    title: 'Matrix Board',
    icon: TreePine,
    altText: 'Matrix board light sequences',
    description: 'Sequences for LED matrix displays'
  }
];

export const popularCombinations: Combination[] = [
  {
    title: 'Christmas Rock Songs',
    href: '/search?holiday=christmas&genre=rock',
    description: 'Rock around the Christmas tree with these energetic sequences'
  },
  {
    title: 'Halloween Hip-Hop Shows',
    href: '/search?holiday=halloween&genre=hip-hop',
    description: 'Spooky beats for your Halloween light display'
  },
  {
    title: 'Christmas Pixel Tree Displays',
    href: '/search?holiday=christmas&prop=pixel-tree',
    description: 'Beautiful sequences designed for pixel Christmas trees'
  },
  {
    title: 'Jazz Singing Faces',
    href: '/search?genre=jazz&type=singing-faces',
    description: 'Smooth jazz performances with animated singing faces'
  }
];

// Helper function to generate SEO-friendly titles
export function generatePageTitle(params: Record<string, string | undefined>): string {
  const parts: string[] = [];
  
  if (params.holiday) {
    const holiday = holidays.find(h => h.id === params.holiday?.toLowerCase());
    if (holiday) parts.push(holiday.title);
  }
  
  if (params.genre) {
    const genre = genres.find(g => g.id === params.genre?.toLowerCase());
    if (genre) parts.push(genre.title);
  }
  
  if (params.type) {
    const type = sequenceTypes.find(t => t.id === params.type?.toLowerCase());
    if (type) parts.push(type.title);
  }
  
  if (params.prop) {
    const prop = props.find(p => p.id === params.prop?.toLowerCase());
    if (prop) parts.push(`for ${prop.title}`);
  }
  
  parts.push('Light Sequences');
  return parts.join(' ') + ' - Light Display Market';
}

// Helper function to generate meta descriptions
export function generateMetaDescription(params: Record<string, string | undefined>): string {
  const parts: string[] = [];
  let context = '';
  
  if (params.holiday) {
    const holiday = holidays.find(h => h.id === params.holiday?.toLowerCase());
    if (holiday) {
      parts.push(holiday.title);
      context = holiday.description;
    }
  }
  
  if (params.genre) {
    const genre = genres.find(g => g.id === params.genre?.toLowerCase());
    if (genre) {
      parts.push(genre.title);
      if (!context) context = genre.description;
    }
  }
  
  if (params.type) {
    const type = sequenceTypes.find(t => t.id === params.type?.toLowerCase());
    if (type) {
      parts.push(type.title);
      if (!context) context = type.description;
    }
  }
  
  if (params.prop) {
    const prop = props.find(p => p.id === params.prop?.toLowerCase());
    if (prop) {
      parts.push(`using ${prop.title}`);
      if (!context) context = prop.description;
    }
  }
  
  const prefix = parts.length > 0 
    ? `Find ${parts.join(' ')} light sequences`
    : 'Browse our collection of light sequences';
    
  return `${prefix} for your holiday light display. ${context} Download and sync your lights to music today.`;
} 